import { useEffect, useRef, useState } from 'react';
import { globalSearch, SearchResponse } from '../services/searchService';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

interface State {
  query: string;
  data: SearchResponse | null;
  isLoading: boolean;
  error: string | null;
}

const emptyState: State = {
  query: '',
  data: null,
  isLoading: false,
  error: null,
};

/**
 * Debounced global-search hook. Cancels in-flight requests when the query
 * changes so only the latest input wins. Returns an idle state for queries
 * shorter than MIN_CHARS so the caller can show "Quick access" instead.
 */
export function useGlobalSearch(rawQuery: string) {
  const [state, setState] = useState<State>(emptyState);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const query = rawQuery.trim();

    if (query.length < MIN_CHARS) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setState({ query, data: null, isLoading: false, error: null });
      return;
    }

    setState((s) => ({ ...s, query, isLoading: true, error: null }));

    const timer = window.setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      globalSearch(query, { signal: controller.signal })
        .then((data) => {
          if (controller.signal.aborted) return;
          setState({ query, data, isLoading: false, error: null });
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          // axios uses `code === 'ERR_CANCELED'` for cancellations
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
          setState({
            query,
            data: null,
            isLoading: false,
            error: err?.response?.data?.message || err?.message || 'Search failed',
          });
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [rawQuery]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return state;
}

export const SEARCH_MIN_CHARS = MIN_CHARS;
